import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { DragIndicator, MoreHoriz } from '@material-ui/icons';
import CreateIcon from '@material-ui/icons/Create';
import TablePaginationActions from '../../../../../shared/table-pagination/TablePaginationActions';
import DeletePopupModel from '../../../../../shared/delete-popup/Delete-Popup';
import { useSelector, useDispatch } from 'react-redux';
import {
  allAmenityCategoriesSelector,
  allAmenitySectionsSelector,
  fetchAllAmenities,
  setAllAmenitySections,
} from '../../../../property/propertySlice';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import {
  AMENITIES_SECTION_CREATE,
  AMENITIES_SECTION_EDIT,
  AMENITIES_SECTION_VIEW,
  checkUsageInCollection,
  DELETE_MESSAGES,
  DESCRIPTION,
  TITLE,
} from '../../../../../constants/AmenityConst';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import {
  createDataSortMapping,
  deleteAmenitySectionAPI,
} from '../../../../../utils/PropertyAPIUtils';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_HOTEL_AMENITIES,
} from '../../../../../constants/RoleConsts';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import useOutsideAlerter from '../../../../../custom-hooks/useOutsideAlerter';
import SearchBar from '../../../../../shared/search-bar/Search-Bar';
import { handleOnDragEnd } from '../../../../../shared/custom-dragdrop/dragDrop';
import { isDragDisabled } from '../../../../../utils/formUtils';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import WarningPopup from '../../../../../shared/warning-popup/Warning-Popup';
import { CONFIRM_DELETE_TITLE } from '../../../../../utils/Constants';
const AmenitySectionList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    selectedHotel,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
  } = props;
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
  });
  const originalSections = useSelector(allAmenitySectionsSelector);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);
  const prevHotelIdRef = useRef(selectedHotel);
  const [popUp, setPopUp] = useState(false);
  const [visible, setVisible] = useState(false);
  const originalCategories = useSelector(allAmenityCategoriesSelector);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setSections(
      originalSections?.map((section) => {
        return {
          ...section,
          isChecked: false,
        };
      }),
    );
    setLoading(false);
    setSearchText('');
  }, [originalSections]);

  useEffect(() => {
    if (loading || sections?.length === 0) return;
    const totalItems = sections?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [sections?.length, rowsPerPage, page, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== selectedHotel) {
      onsetPage(0);
    }
    prevHotelIdRef.current = selectedHotel;
  }, [selectedHotel]);

  const handleSearchText = (searchText) => {
    setSearchText(searchText);
    const filteredRows = originalSections?.filter((item) => {
      return item?.name?.toLowerCase()?.includes(searchText?.toLowerCase());
    });
    onsetPage(0);
    setSections(filteredRows);
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      category: null,
      warn: false,
    });
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event?.target?.value, 10));
    onsetPage(0);
  };
  const onOpenDrawer = (event, type) => {
    event.stopPropagation();
    onDrawerToggle(type, true, selectedValue?.section);
    handleClose();
  };

  const handleCellMenuClick = (event, section) => {
    event.stopPropagation();
    setSelectedValue({
      id: section?.id,
      name: section?.name,
      title: '',
      description: '',
      status: false,
      anchorEl: event?.currentTarget,
      section: section,
    });
  };

  const checkUsageInCategories = () => {
    return checkUsageInCollection(originalCategories, 'sectionIds', selectedValue);
  };
  const deleteAmenitySection = async (e) => {
    e.preventDefault();
    if (checkUsageInCategories()) {
      setSelectedValue((prev) => ({
        ...prev,
        title: CONFIRM_DELETE_TITLE,
        description: DELETE_MESSAGES.amenitySection.cannotDelete,
        status: false,
        warn: true,
        anchorEl: null,
      }));
    } else {
      setSelectedValue((prev) => ({
        ...prev,
        title: CONFIRM_DELETE_TITLE,
        description: DELETE_MESSAGES.amenitySection.irreversible,
        status: true,
        warn: false,
        anchorEl: null,
      }));
    }
  };

  const deleteSection = async () => {
    try {
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      if (selectedHotel && selectedValue && selectedValue?.id) {
        const res = await deleteAmenitySectionAPI(selectedValue?.id, selectedHotel, language);
        if (res?.data?.deleteCategorySection.status === 'Success') {
          dispatch(fetchAllAmenities(selectedHotel));
          notificationManager('success', 'Section deleted successfully');
          handleClose();
        } else if (res?.data?.deleteCategorySection.status === 'Failed') {
          notificationManager('danger', 'Failed to delete Section');
          handleClose();
        }
      }
    } catch (e) {
      if (e.graphQLErrors) {
        notificationManager('danger', e.graphQLErrors[0].message);
      } else {
        notificationManager('danger', 'Failed to delete Section');
      }
      handleClose();
    }
  };

  const onDeleteCancel = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      section: null,
    });
  };

  const dragDisabled = isDragDisabled(sections, searchText);

  const onViewDetails = (section, e) => {
    if (e?.target?.tagName === 'TD') {
      onViewToggle(AMENITIES_SECTION_VIEW, true, section);
    }
  };

  const onClickView = (e) => {
    if (selectedValue && selectedValue.section) {
      onViewToggle(AMENITIES_SECTION_VIEW, true, selectedValue?.section);
    }
  };

  const retrieveNoOfCategories = (section) => {
    let counter = {};
    let count = 0;
    originalCategories?.forEach((room) =>
      room?.sectionIds?.forEach((item) => {
        item === section?.id && (counter[section?.name] = count += 1);
      }),
    );
    return counter[section?.name] ? counter[section?.name] : 0;
  };

  useOutsideAlerter(wrapperRef, visible, setPopUp);

  return (
    <div className='mt-4 mb-4 group-list-container'>
      {selectedValue.id && selectedValue?.status === true && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={deleteSection}
          onDeleteCancel={onDeleteCancel}
        />
      )}
      {selectedValue?.id && selectedValue?.warn && (
        <WarningPopup
          isTitleNameShow={false}
          warningPopup={selectedValue}
          onWarningClose={handleClose}
          warn={true}
        />
      )}
      <SearchBar
        type={AMENITIES_SECTION_CREATE}
        buttonName='Add Section'
        displayAddOrEdit={
          permissions[RBAC_TYPE_HOTEL_AMENITIES] === RBAC_PERMISSION_LEVEL_FULL_ACCESS
            ? 'show'
            : 'hide'
        }
        onOpenDrawer={onOpenDrawer}
        setSearchText={handleSearchText}
        visible={visible}
        setVisible={setVisible}
        popUp={popUp}
        setPopUp={setPopUp}
        placeHolder={sections}
        setPlaceHolder={setSections}
        globalPlaceHolder={originalSections}
        setGlobalPlaceHolder={setAllAmenitySections}
        dispatch={dispatch}
        mappingApi={createDataSortMapping}
        displayText='Hotel Compendium section'
        module='AMENITY_SECTION'
        searchText={searchText}
        setSearch={setSearchText}
        actionMenuItems={[]}
      />

      <div ref={wrapperRef} className='row mx-2 d-flex mt-4'>
        {loading ? (
          <div className='pt-4 col-md-12'>
            <LinearProgressBar />
          </div>
        ) : (
          sections && (
            <TableContainer>
              <Table className='list-table' aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell className='w-40'>Name</TableCell>
                    <TableCell className='w-50'>No.of categories</TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>

                <DragDropContext
                  onDragEnd={(event) =>
                    handleOnDragEnd(
                      event,
                      setVisible,
                      sections,
                      setSections,
                      false,
                      page,
                      rowsPerPage,
                    )
                  }
                  isDropDisabled={dragDisabled}
                >
                  <Droppable droppableId='spas' isDropDisabled={dragDisabled}>
                    {(provided) => (
                      <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                        {(rowsPerPage > 0
                          ? sections.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          : sections
                        ).map((section, i) => (
                          <Draggable
                            key={section?.id}
                            draggableId={section?.id}
                            index={i}
                            isDragDisabled={dragDisabled}
                          >
                            {(provided) => (
                              <TableRow
                                key={i}
                                className='border-white pointer'
                                onClick={(e) => {
                                  onViewDetails(section, e);
                                }}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <TableCell>
                                  {!dragDisabled && (
                                    <DragIndicator className='res-menu-accordion-drag ' />
                                  )}
                                </TableCell>
                                <TableCell className='w-40'>{section?.name}</TableCell>
                                <TableCell className='w-50'>
                                  {retrieveNoOfCategories(section)}
                                </TableCell>
                                <TableCell align='right'>
                                  {permissions[RBAC_TYPE_HOTEL_AMENITIES] ===
                                    RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                                    <Button
                                      aria-controls='simple-menu'
                                      aria-haspopup='true'
                                      onClick={(e) => {
                                        handleCellMenuClick(e, section);
                                      }}
                                      className='showpopupInner'
                                    >
                                      <MoreHoriz className='showpopupInner' />
                                    </Button>
                                  )}
                                  <Menu
                                    id={i}
                                    anchorEl={selectedValue?.anchorEl}
                                    disableRestoreFocus
                                    MenuListProps={{ autoFocusItem: false }}
                                    keepMounted
                                    open={
                                      Boolean(selectedValue.anchorEl) &&
                                      selectedValue.id === section?.id
                                    }
                                    onClose={handleClose}
                                    className='shadow'
                                  >
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onOpenDrawer(e, AMENITIES_SECTION_EDIT);
                                      }}
                                    >
                                      <CreateIcon className='me-2 action-menu-icon' />
                                      Edit
                                    </MenuItem>
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onClickView(e);
                                      }}
                                    >
                                      <VisibilityIcon className='me-2 action-menu-icon' />
                                      View
                                    </MenuItem>
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => deleteAmenitySection(e)}
                                    >
                                      <DeleteForeverIcon className='me-2 action-menu-icon' />
                                      Delete
                                    </MenuItem>
                                  </Menu>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </DragDropContext>
                <TableFooter>
                  <TableRow className='paginator'>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                      count={sections?.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: { 'aria-label': 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )
        )}
      </div>
    </div>
  );
};

export default AmenitySectionList;
